import {
  isYoutubeCategory,
  YOUTUBE_CATEGORIES_US_REGION,
} from '~publish/legacy/composer/composer/components/youtube/constants'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import type {
  YoutubeCategory,
  YoutubeChannelData,
} from '~publish/legacy/composer/composer/entities/Draft/types'

const DEFAULT_YOUTUBE_CHANNEL_DATA: YoutubeChannelData = {
  title: '',
  category: YOUTUBE_CATEGORIES_US_REGION[0],
  license: 'youtube',
  privacyStatus: 'public',
  notifySubscribers: true,
  embeddable: true,
  madeForKids: false,
}

const validateLicense = (value: unknown): 'youtube' | 'creativeCommon' => {
  return value === 'youtube' || value === 'creativeCommon'
    ? value
    : DEFAULT_YOUTUBE_CHANNEL_DATA.license
}

const validatePrivacyStatus = (
  value: unknown,
): 'public' | 'private' | 'unlisted' => {
  return value === 'public' || value === 'private' || value === 'unlisted'
    ? value
    : DEFAULT_YOUTUBE_CHANNEL_DATA.privacyStatus
}

const validateBoolean = (value: unknown): boolean => {
  return typeof value === 'boolean' ? value : true
}

const validateCategory = (value: unknown): YoutubeCategory => {
  if (isYoutubeCategory(value)) {
    // Check if the value matches one of the categories in the list
    const matchingCategory = YOUTUBE_CATEGORIES_US_REGION.find(
      (category) => category.id === value.id && category.title === value.title,
    )
    if (matchingCategory) {
      return matchingCategory
    }
  }

  // If the value is not valid, return the first category as default
  return DEFAULT_YOUTUBE_CHANNEL_DATA.category
}

export const saveYoutubeDefaultsToStorage = (drafts: Draft[]): void => {
  const youtubeDrafts = drafts.filter((draft) => draft.id === 'youtube')
  const youtubeDraft =
    youtubeDrafts && youtubeDrafts.length > 0 ? youtubeDrafts[0] : null
  if (youtubeDraft) {
    const { channelData } = youtubeDraft
    const youtube = channelData?.youtube ?? DEFAULT_YOUTUBE_CHANNEL_DATA
    // Runs after the post is saved server-side, so a full or blocked storage
    // must not surface as a save failure.
    try {
      localStorage.setItem('youtube_defaults', JSON.stringify(youtube))
    } catch {
      // Best-effort: the next draft falls back to the previous defaults.
    }
  }
}

export const getYoutubeDefaultsFromStorage = (): YoutubeChannelData => {
  const defaults = localStorage.getItem('youtube_defaults')
  if (defaults) {
    const parsedDefaults = JSON.parse(defaults)

    // Validate parsedDefaults.
    return {
      title: '',
      category: validateCategory(parsedDefaults.category),
      license: validateLicense(parsedDefaults.license),
      privacyStatus: validatePrivacyStatus(parsedDefaults.privacyStatus),
      notifySubscribers: validateBoolean(parsedDefaults.notifySubscribers),
      embeddable: validateBoolean(parsedDefaults.embeddable),
      madeForKids: validateBoolean(parsedDefaults.madeForKids),
    }
  }

  return DEFAULT_YOUTUBE_CHANNEL_DATA
}
